import tw from 'twin.macro';
import { Trans } from 'react-i18next';

import { ToolTip } from '@insights_module/components/ToolTip';
import { AudioPlayer } from '@insights_module/components/episode_details_screen/AudioPlayer';
import { Info } from '@icons/Info';
import {
  DETAILS_VIEW_ITEMS,
  PROMOTION_VIEW_ITEMS,
  useEpisodeDetailsContext,
} from '@insights_module/context/EpisodeDetailsContext';
import {
  DownloadChaptersButton,
  EpisodesChaptersItem,
} from '@insights_module/components/episode_details_screen/EpisodeChaptersItem';
import { EpisodePromotionItem } from '@insights_module/components/episode_details_screen/EpisodePromotionItem';
import { CopyGeneratedContentButton } from '@insights_module/components/episode_details_screen/CopyGeneratedContentButton';
import {
  DownloadTopicsButton,
  EpisodesTopicsItem,
} from '@insights_module/components/episode_details_screen/EpisodeTopicsItem';
import {
  DownloadKeywordsButton,
  EpisodesKeywordsItem,
} from '@insights_module/components/episode_details_screen/EpisodeKeywordsItem';
import { useEpisodeUrl } from '@insights_module/hooks/useEpisodeUrl';
import { FEATURE_FLAG, useFeatureFlag } from '@auth_module/hooks/useFeatureFlag';
import { useEpisodeDetailsData } from '@insights_module/hooks/useEpisodeDetailsData';

const Container = tw.div`flex items-start self-stretch h-full`;
const MainContentContainer = tw.div`relative flex flex-col items-start self-stretch h-full border-r border-r-gray-3 bg-neutral-background flex-1`;
const MainContentHeader = tw.div`flex px-[24px] h-[46px] min-h-[46px] items-center justify-between border-b border-b-gray-3 w-full text-text-and-icon font-bold text-[16px]`;
const ScrollableWrapper = tw.div`overflow-scroll w-full h-full`;
const ContentSidebar = tw.div`flex flex-col w-[300px] h-full p-[24px] pb-0 gap-[16px] transition-all duration-200`;
const Divider = tw.div`h-[1px] w-full bg-gray-1`;
const SelectedItemInfo = tw.div`flex w-[32px] h-[32px] justify-center items-center rounded-full cursor-pointer`;
const PlayerContainer = tw.div`w-full p-[8px]`;

const HEADER_MAPPER = {
  twitter: {
    TITLE: <Trans i18nKey={'insights.episode_details_drawer.main_content_titles.twitter'} />,
  },
  facebook: {
    TITLE: <Trans i18nKey={'insights.episode_details_drawer.main_content_titles.facebook'} />,
  },
  linkedin: {
    TITLE: <Trans i18nKey={'insights.episode_details_drawer.main_content_titles.linkedin'} />,
  },
  instagram: {
    TITLE: <Trans i18nKey={'insights.episode_details_drawer.main_content_titles.instagram'} />,
  },
  show_notes: {
    TITLE: <Trans i18nKey={'insights.episode_details_drawer.main_content_titles.show_notes'} />,
  },
  summary: {
    TITLE: <Trans i18nKey={'insights.episode_details_drawer.main_content_titles.summary'} />,
  },
  article: {
    TITLE: <Trans i18nKey={'insights.episode_details_drawer.main_content_titles.article'} />,
  },
  chapters: {
    TITLE: <Trans i18nKey={'insights.episode_details_drawer.main_content_titles.chapters'} />,
  },
  topics: {
    TITLE: <Trans i18nKey={'insights.episode_details_drawer.main_content_titles.topics'} />,
    TOOLTIP: <Trans i18nKey={'insights.episode_details_drawer.main_content_tooltips.topics'} />,
  },
  keywords: {
    TITLE: <Trans i18nKey={'insights.episode_details_drawer.main_content_titles.keywords'} />,
    TOOLTIP: <Trans i18nKey={'insights.episode_details_drawer.main_content_tooltips.keywords'} />,
  },
} as { [key: string]: { TITLE: JSX.Element; TOOLTIP?: JSX.Element } };

const Disclaimer = () => {
  const { activeView } = useEpisodeDetailsContext();

  if (!(DETAILS_VIEW_ITEMS.includes(activeView) || PROMOTION_VIEW_ITEMS.includes(activeView)))
    return null;

  return (
    <>
      <Divider />
      <div tw='flex p-[12px] items-center self-stretch bg-disc-bg rounded-md'>
        <div tw='text-[12px] text-disc-text-and-icon rounded-md'>
          <Trans i18nKey='insights.episode_details_drawer.promotion_view.disclaimer'>
            <span tw='font-semibold'>DISCLAIMER:</span> We are constantly working to improve the
            accuracy and quality of our AI-generated content. However, there may still be errors or
            inaccuracies.
          </Trans>
        </div>
      </div>
    </>
  );
};

export const EpisodeDetailsAndPromotionContent = () => {
  const { isEnabled: isEpisodePromotionEnabled } = useFeatureFlag(
    FEATURE_FLAG.EPISODE_DETAILS_PROMOTION_SECTION
  );
  const { activeView, selectedSocialPostType, selectedPromotionItemIsGenerating } =
    useEpisodeDetailsContext();
  const { selectedPromotionItem } = useEpisodeDetailsData();
  const { url } = useEpisodeUrl();

  const title =
    HEADER_MAPPER[activeView === 'social_posts' ? selectedSocialPostType : activeView]?.TITLE;
  const tooltip =
    HEADER_MAPPER[activeView === 'social_posts' ? selectedSocialPostType : activeView]?.TOOLTIP;

  const shouldHaveRightPadding = PROMOTION_VIEW_ITEMS.includes(activeView);

  return (
    <Container>
      <MainContentContainer>
        <MainContentHeader>
          {title}
          {!!tooltip && (
            <SelectedItemInfo>
              <ToolTip content={<span>{tooltip}</span>} placement='bottom'>
                <Info tw='stroke-text-and-icon-muted w-[24px] h-[24px]' />
              </ToolTip>
            </SelectedItemInfo>
          )}
        </MainContentHeader>

        <ScrollableWrapper
          css={[
            shouldHaveRightPadding &&
              !selectedPromotionItemIsGenerating &&
              !!selectedPromotionItem?.value &&
              tw`pr-[80px] xl:pr-[20%]`,
          ]}>
          {isEpisodePromotionEnabled && <EpisodePromotionItem />}
          <EpisodesChaptersItem />
          <EpisodesTopicsItem />
          <EpisodesKeywordsItem />
        </ScrollableWrapper>

        {activeView === 'chapters' && (
          <PlayerContainer>
            <AudioPlayer url={url} />
          </PlayerContainer>
        )}
      </MainContentContainer>

      <ContentSidebar>
        <CopyGeneratedContentButton />
        <DownloadChaptersButton />
        <DownloadTopicsButton />
        <DownloadKeywordsButton />

        <Disclaimer />
      </ContentSidebar>
    </Container>
  );
};
